import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  Mail, 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  Building,
  TrendingUp,
  BarChart3,
  BookOpen,
  Shield,
  Users,
  Award,
  ArrowRight,
  CheckCircle,
  Play,
  Star,
  Sparkles,
  Zap,
  Target,
  Globe,
  UserPlus
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import api from '../../services/api';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { register: registerUser, error, clearError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const onSubmit = async (data) => {
    console.log('=== FORM SUBMISSION STARTED ===');
    console.log('Form data received:', data);
    setIsLoading(true);
    clearError();
    
    try {
      console.log('Sending registration data:', data);
      console.log('Email being sent:', data.email);
      // Use the new OTP registration endpoint with longer timeout
      const response = await api.post('/auth/register-otp/', data, {
        timeout: 30000 // 30 seconds timeout
      });
      
      console.log('Registration response:', response.data);
      
      if (response.data.message) {
        console.log('Redirecting to OTP verification page...');
        // Clear the form
        document.querySelector('form').reset();
        // Redirect to OTP verification page with email
        navigate('/verify-otp', { 
          state: { 
            email: data.email,
            message: response.data.message 
          }
        });
      } else {
        console.error('No message in response:', response.data);
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error config:', error.config);
      
      // Set error message for display
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.log('Setting timeout error');
        registerUser(null, null, 'Registration is taking too long. Please check your internet connection and try again.');
      } else if (error.response?.data?.error) {
        // Handle single error message
        console.log('Setting error:', error.response.data.error);
        registerUser(null, null, error.response.data.error);
      } else if (error.response?.data?.non_field_errors) {
        // Handle non-field errors
        console.log('Setting non-field error:', error.response.data.non_field_errors[0]);
        registerUser(null, null, error.response.data.non_field_errors[0]);
      } else if (error.response?.data) {
        // Handle field-specific errors
        const firstError = Object.values(error.response.data)[0];
        if (Array.isArray(firstError)) {
          console.log('Setting field error:', firstError[0]);
          registerUser(null, null, firstError[0]);
        } else {
          console.log('Setting field error:', firstError);
          registerUser(null, null, firstError);
        }
      } else {
        console.log('Setting generic error');
        registerUser(null, null, 'Registration failed. Please try again.');
      }
    } finally {
    setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-slate-50/30 to-blue-50/20 relative overflow-hidden">
      {/* Professional White & Blue Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Elegant Blue Gradient Orbs */}
        <div 
          className="absolute w-[600px] h-[600px] bg-gradient-to-r from-blue-200/20 via-indigo-100/15 to-slate-200/10 rounded-full blur-3xl animate-pulse"
          style={{
            left: `${mousePosition.x / 40}px`,
            top: `${mousePosition.y / 40}px`,
            transform: 'translate(-50%, -50%)'
          }}
        />
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-gradient-to-r from-indigo-200/15 to-blue-100/10 rounded-full blur-3xl animate-pulse animation-delay-2000" />
        <div className="absolute bottom-1/3 right-1/3 w-[550px] h-[550px] bg-gradient-to-r from-slate-300/12 to-blue-200/8 rounded-full blur-3xl animate-pulse animation-delay-4000" />
        
        {/* Professional Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.03'%3E%3Ccircle cx='40' cy='40' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '80px 80px'
          }} />
        </div>

        {/* Elegant Light Rays */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-blue-200/20 to-transparent" />
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-indigo-300/15 to-transparent" />
        <div className="absolute top-0 left-2/3 w-px h-full bg-gradient-to-b from-transparent via-slate-200/10 to-transparent" />
      </div>

      {/* Professional Navigation */}
      <nav className="relative z-10 px-6 py-4 flex justify-between items-center backdrop-blur-xl bg-white/80 border-b border-blue-100/50">
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group hover:scale-110 transition-all duration-300">
              <span className="text-white font-bold text-lg group-hover:scale-110 transition-transform duration-300">ET</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">EducateTrade</h1>
              <p className="text-xs text-gray-600 font-medium">Professional LSE Trading Platform</p>
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-8">
          <a href="#features" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">Features</a>
          <a href="#about" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">About</a>
          <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-all duration-300 hover:scale-105 font-medium text-sm">Contact</a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-100px)] py-8">
          
          {/* Left Side - Professional Hero Content */}
          <div className="space-y-8 fadeInUp">
              <div className="space-y-6">
              <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-100/80 via-indigo-50/60 to-slate-100/80 backdrop-blur-xl border border-blue-200/50 text-gray-700 rounded-full text-sm font-semibold shadow-lg shadow-blue-200/30">
                <Sparkles className="h-4 w-4 mr-2 text-blue-600" />
                Professional LSE Trading Education Platform
                </div>
                
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 leading-tight tracking-tight">
                  Join
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 animate-gradient"> EducateTrade</span>
                </h1>
                
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl font-light">
                  Start your trading journey with our comprehensive educational platform. Learn professional strategies, practice with real-time LSE data, and build your portfolio.
                </p>
              </div>

            {/* Professional Features Grid */}
              <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-6 w-6 text-blue-800" />
                </div>
                <span className="text-sm font-bold text-gray-700">Real-time LSE Data</span>
                  </div>
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-indigo-100 to-slate-200 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-6 w-6 text-indigo-800" />
                </div>
                <span className="text-sm font-bold text-gray-700">Risk-Free Practice</span>
                  </div>
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-slate-100 to-blue-200 rounded-xl flex items-center justify-center shadow-lg shadow-slate-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Globe className="h-6 w-6 text-slate-700" />
                </div>
                <span className="text-sm font-bold text-gray-700">Fresh News</span>
                  </div>
              <div className="flex items-center space-x-3 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 hover:bg-white/80 transition-all duration-300 group hover:scale-105">
                <div className="h-12 w-12 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200/30 group-hover:scale-110 transition-transform duration-300">
                  <Target className="h-6 w-6 text-blue-700" />
                </div>
                <span className="text-sm font-bold text-gray-700">Portfolio Tracking</span>
                </div>
              </div>
            </div>

          {/* Right Side - Professional Register Form */}
          <div className="slideInRight">
            <div className="relative">
              {/* Professional Glass Morphism Card */}
              <div className="bg-white/80 backdrop-blur-2xl rounded-3xl shadow-2xl border border-blue-100/50 p-8 hover:bg-white/90 transition-all duration-500 group">
                {/* Professional Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-200/20 via-indigo-100/15 to-slate-200/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10" />
                
              <div className="text-center mb-8">
                  <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-700 mb-6 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-2xl">ET</span>
                </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-3 tracking-tight">
                  Create Your Account
                </h2>
                  <p className="text-gray-600 text-lg font-light">
                  Join EducateTrade and start your trading education
                </p>
              </div>

                              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-4">
                                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="relative group">
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                          </div>
                          <input
                            type="text"
                            placeholder="Enter your first name"
                            className="w-full pl-10 pr-3 py-3 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-sm"
                      {...register('first_name', {
                        required: 'First name is required',
                      })}
                    />
                        </div>
                        {errors.first_name && (
                          <p className="mt-2 text-sm text-red-500 font-medium">{errors.first_name.message}</p>
                        )}
                      </div>

                                            <div className="relative group">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-4 w-4 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                          </div>
                          <input
                            type="text"
                            placeholder="Enter your last name"
                            className="w-full pl-10 pr-3 py-3 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-xl text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-sm"
                      {...register('last_name', {
                        required: 'Last name is required',
                      })}
                    />
                        </div>
                        {errors.last_name && (
                          <p className="mt-2 text-sm text-red-500 font-medium">{errors.last_name.message}</p>
                        )}
                      </div>
                  </div>

                                        <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Username
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-4 w-4 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                        </div>
                        <input
                          type="text"
                          placeholder="Choose a username"
                          className="w-full pl-10 pr-3 py-3 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-xl text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-sm"
                    {...register('username', {
                      required: 'Username is required',
                      minLength: {
                        value: 3,
                        message: 'Username must be at least 3 characters',
                      },
                      pattern: {
                        value: /^[a-zA-Z0-9_]+$/,
                        message: 'Username can only contain letters, numbers, and underscores',
                      },
                    })}
                  />
                      </div>
                      {errors.username && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.username.message}</p>
                      )}
                    </div>

                                        <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                        </div>
                        <input
                          type="email"
                          placeholder="Enter your email"
                          className="w-full pl-10 pr-3 py-3 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-xl text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-sm"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                      </div>
                      {errors.email && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.email.message}</p>
                      )}
                    </div>

                                        <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a password"
                          className="w-full pl-10 pr-10 py-3 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-xl text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-sm"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                      },
                    })}
                  />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-900 transition-colors duration-300"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.password.message}</p>
                      )}
                    </div>

                                        <div className="relative group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-gray-400 group-focus-within:text-blue-900 transition-colors duration-300" />
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          className="w-full pl-10 pr-10 py-3 bg-white/80 backdrop-blur-xl border-2 border-blue-100/50 rounded-xl text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 hover:bg-white/90 font-medium text-sm"
                    {...register('confirm_password', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === password || 'Passwords do not match',
                    })}
                  />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-900 transition-colors duration-300"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.confirm_password && (
                        <p className="mt-2 text-sm text-red-500 font-medium">{errors.confirm_password.message}</p>
                      )}
                    </div>
                </div>

                  {error && (
                    <div className="rounded-2xl bg-red-500/15 backdrop-blur-xl p-4 border border-red-500/20">
                      <div className="flex items-center">
                        <div className="ml-3">
                          <h3 className="text-sm font-semibold text-red-300">
                            {error}
                          </h3>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-5 bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 hover:from-blue-700 hover:via-indigo-700 hover:to-slate-800 text-white font-bold text-base rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none btn-premium"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        <span>Creating Account...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <UserPlus className="h-4 w-4 mr-2" />
                        <span>Create EducateTrade Account</span>
                      </div>
                    )}
                  </button>

                  <div className="text-center mt-6 p-4 rounded-2xl bg-white/60 backdrop-blur-xl border border-blue-100/50 nav-link-entrance">
                    <p className="text-gray-700 text-lg font-medium">
                      Already have an account?{' '}
                      <Link
                        to="/login"
                        className="inline-block font-bold text-blue-600 hover:text-blue-700 transition-all duration-300 hover:scale-105 hover:text-shadow-lg hover:drop-shadow-lg"
                        style={{
                          textShadow: '0 0 10px rgba(59, 130, 246, 0.5)',
                          filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.3))'
                        }}
                      >
                        Sign in here
                      </Link>
                    </p>
                    <div className="mt-2 text-sm text-gray-600">
                      Welcome back, trader!
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Features Section */}
      <div id="features" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-100/80 via-indigo-50/60 to-slate-100/80 backdrop-blur-xl border border-blue-200/50 text-gray-700 rounded-full text-sm font-semibold shadow-lg shadow-blue-200/30 mb-6">
              <Star className="h-4 w-4 mr-2 text-blue-600" />
              Why Choose EducateTrade?
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6 tracking-tight">
              Professional Trading
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 animate-gradient"> Education</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
              Comprehensive tools and resources to help you master LSE trading with confidence
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group text-center p-8 rounded-3xl bg-white/60 backdrop-blur-2xl border border-blue-100/50 hover:bg-white/80 transition-all duration-500 hover:scale-105 card-premium">
              <div className="h-20 w-20 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200/30 group-hover:scale-110 transition-transform duration-300">
                <BarChart3 className="h-10 w-10 text-blue-800" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide">Real Market Data</h3>
              <p className="text-gray-600 leading-relaxed font-light">Access live LSE data and practice with real market conditions in a risk-free environment</p>
            </div>

            <div className="group text-center p-8 rounded-3xl bg-white/60 backdrop-blur-2xl border border-blue-100/50 hover:bg-white/80 transition-all duration-500 hover:scale-105 card-premium">
              <div className="h-20 w-20 bg-gradient-to-br from-indigo-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-200/30 group-hover:scale-110 transition-transform duration-300">
                <BookOpen className="h-10 w-10 text-indigo-800" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide">Fresh Market News</h3>
              <p className="text-gray-600 leading-relaxed font-light">Stay updated with latest market news and insights during trading hours</p>
            </div>

            <div className="group text-center p-8 rounded-3xl bg-white/60 backdrop-blur-2xl border border-blue-100/50 hover:bg-white/80 transition-all duration-500 hover:scale-105 card-premium">
              <div className="h-20 w-20 bg-gradient-to-br from-slate-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-slate-200/30 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4 tracking-wide">Risk-Free Learning</h3>
              <p className="text-gray-600 leading-relaxed font-light">Practice trading strategies without financial risk and build confidence</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 